// A mate's workout, in full, with the three things you can do with it.
//
// The feed card is a headline — a title, a few chips, three movement names.
// It was also the whole story: there was no way to read what was actually in
// someone's session before deciding whether you wanted it. This is the
// article, and it answers the question the card raises.
//
// The plan rendered here is the snapshot stored on the post, never the
// author's live session row (see createSessionFeedPost): someone editing their
// own history later must not rewrite what their friends were shown.
//
// Layout follows IncomingLinkDialog, which solved the same problem for share
// links — a deep plan with long labels is wider than a phone, so the tree
// scrolls sideways in its own box while the dialog body scrolls down.

import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Check, ClipboardList, Clock, Dumbbell, Loader2, Save } from "lucide-react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useToast } from "../../contexts/ToastContext";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import ShareSessionButton from "../share/ShareSessionButton";
import MetricChips from "./MetricChips";
import { summariseWorkoutPost } from "../../utils/feedSummary";
import { calculateTotalDuration, formatDuration, timeAgo } from "../../utils/time";
import { copyWorkout } from "../../services/api";

const WorkoutPostDialog = ({ post, open, onOpenChange }) => {
  const { loadSessionFromData } = useContext(WorkoutContext);
  const { showToast } = useToast();

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Reopening the same post must offer the action again rather than showing a
  // stale tick from last time.
  useEffect(() => {
    if (open) {
      setSaving(false);
      setSaved(false);
      setError(null);
    }
  }, [open]);

  const plan = post?.plan_data || null;
  const summary = summariseWorkoutPost(post);
  const totalSeconds = plan ? calculateTotalDuration(plan) : 0;
  const sectionCount = Array.isArray(plan?.children) ? plan.children.length : 0;

  const handleSave = useCallback(async () => {
    if (!post?.id) return;
    setSaving(true);
    setError(null);
    try {
      await copyWorkout(post.id);
      setSaved(true);
      showToast({
        title: "Saved to your sessions",
        description: "Pick it up any time from Session → Load Session.",
      });
    } catch (err) {
      console.error("Copy workout failed:", err);
      setError(err.message || "Could not save this workout.");
    } finally {
      setSaving(false);
    }
  }, [post?.id, showToast]);

  // Lands in the Session tab rather than starting immediately: it is built
  // around someone else's numbers, and the loads are the first thing most
  // people will want to change.
  const handleUse = useCallback(() => {
    if (!plan) return;
    loadSessionFromData(plan);
    showToast({
      title: "Session loaded",
      description: "Adjust it in the Session tab, then start when you're ready.",
    });
    onOpenChange(false);
  }, [plan, loadSessionFromData, showToast, onOpenChange]);

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The explicit row template pins the header and footer while only the
          middle scrolls; min-w-0 stops the grid track stretching past the
          viewport on a wide plan and clipping the buttons off the right. */}
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden p-4 sm:p-6">
        <DialogHeader className="min-w-0 pr-8 text-left">
          <DialogTitle className="min-w-0 break-words pr-2">
            {summary.title || "Workout"}
          </DialogTitle>
          <DialogDescription className="min-w-0 break-words">
            {post.author_name
              ? `${post.author_name} · ${timeAgo(post.created_at)}`
              : timeAgo(post.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 min-h-0 overflow-y-auto space-y-3">
          <MetricChips metrics={summary.metrics} />

          {/* What they wrote, in full. The card shows a 160-character excerpt
              because a card is a headline; here there is room for the whole
              thing, which is often the only human line in the record. */}
          {post.notes && (
            <p className="text-sm italic text-muted-foreground border-l-2 border-border pl-3 leading-snug whitespace-pre-line break-words">
              {post.notes}
            </p>
          )}

          {plan ? (
            <div className="space-y-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDuration(totalSeconds) || "—"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Dumbbell className="h-3.5 w-3.5" />
                  {sectionCount} section{sectionCount === 1 ? "" : "s"}
                </span>
              </div>
              <div className="min-w-0 overflow-x-auto rounded-lg border border-border p-2">
                <SessionPlanDisplay plan={plan} />
              </div>
            </div>
          ) : (
            // An imported ride or a hand-logged workout: there is a record of
            // it, but no structure anybody could repeat.
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              No plan was attached to this workout, so there's nothing to load
              or save.
            </p>
          )}

          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="min-w-0 gap-2 sm:gap-2 [&>*]:w-full sm:[&>*]:w-auto">
          {plan ? (
            <>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saving || saved}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : saved ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {saved ? "Saved" : "Save"}
              </Button>
              <ShareSessionButton plan={plan} size="default" />
              <Button onClick={handleUse}>
                <ClipboardList className="h-4 w-4 mr-2" />
                Use this
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkoutPostDialog;
